<script lang="ts">
  import { STORE, type WalletStat } from "../../state.svelte";
  import { shortAddress } from "../../utils";
  import { onMount } from "svelte";

  const MAX_BUBBLES = 100;
  const BOT_THRESHOLD = 0.1;
  const WHALE_THRESHOLD = 5;

  let minTokens = $state(1);
  let sortedWallets: WalletStat[] = $state([]);
  let totalVolume = $state(0);
  let totalWallets = $state(0);
  let topWallet: WalletStat | null = $state(null);
  let likelyBots = $state(0);
  let likelyWhales = $state(0);
  let bubbleData: { x: number; y: number; r: number; wallet: WalletStat }[] = $state([]);
  let viewBox = $state("0 0 600 400");

  let hoveredWallet: WalletStat | null = $state(null);
  let selectedWallet: WalletStat | null = $state(null);

  // The wallet to show in tooltip: selected takes priority over hovered
  let activeWallet = $derived(selectedWallet ?? hoveredWallet);

  // Previous positions for smooth interpolation
  let prevPositions = new Map<string, { x: number; y: number; r: number }>();

  onMount(() => {
    const fastIv = setInterval(() => {
      const allWallets = Object.values(STORE.bot.walletStats);
      sortedWallets = [...allWallets]
        .filter((w) => w.tokens.size >= minTokens)
        .sort((a, b) => b.quoteVolume - a.quoteVolume)
        .slice(0, MAX_BUBBLES);
      totalVolume = allWallets.reduce((acc, w) => acc + w.quoteVolume, 0);
      totalWallets = allWallets.length;
      topWallet = sortedWallets.length > 0 ? sortedWallets[0] : null;

      let bots = 0, whales = 0;
      for (const w of allWallets) {
        const avg = w.tradeCount > 0 ? w.quoteVolume / w.tradeCount : 0;
        if (w.tradeCount > 5 && avg < BOT_THRESHOLD) bots++;
        if (avg > WHALE_THRESHOLD) whales++;
      }
      likelyBots = bots;
      likelyWhales = whales;
    }, 1000);

    const slowIv = setInterval(() => {
      if (sortedWallets.length === 0) { bubbleData = []; return; }

      const maxVol = sortedWallets[0].quoteVolume;
      const minRadius = 8, maxRadius = 70;
      const items = sortedWallets.map((w) => {
        const ratio = maxVol > 0 ? w.quoteVolume / maxVol : 0;
        return { wallet: w, r: minRadius + Math.sqrt(ratio) * (maxRadius - minRadius) };
      });

      const placed: { x: number; y: number; r: number; wallet: WalletStat }[] = [];
      const cx = 300, cy = 200;

      for (const item of items) {
        if (placed.length === 0) {
          placed.push({ x: cx, y: cy, r: item.r, wallet: item.wallet });
          continue;
        }

        // Try placing adjacent to each already-placed bubble
        let bestPos: { x: number; y: number } | null = null;
        let bestDist = Infinity;

        for (const p of placed) {
          const targetDist = p.r + item.r + 3;
          // Try 36 angles around each placed bubble
          for (let a = 0; a < 36; a++) {
            const angle = (a / 36) * Math.PI * 2;
            const tx = p.x + Math.cos(angle) * targetDist;
            const ty = p.y + Math.sin(angle) * targetDist;

            let overlaps = false;
            for (const q of placed) {
              const dx = tx - q.x, dy = ty - q.y;
              const md = item.r + q.r + 2;
              if (dx * dx + dy * dy < md * md) { overlaps = true; break; }
            }

            if (!overlaps) {
              const d = Math.hypot(tx - cx, ty - cy);
              if (d < bestDist) {
                bestDist = d;
                bestPos = { x: tx, y: ty };
              }
            }
          }
        }

        if (!bestPos) {
          // Fallback: spiral outward
          let angle = 0, dist = 0;
          for (let step = 0; step < 1000; step++) {
            angle += 0.5;
            dist += 1;
            const tx = cx + Math.cos(angle) * dist;
            const ty = cy + Math.sin(angle) * dist;
            let overlaps = false;
            for (const q of placed) {
              const dx = tx - q.x, dy = ty - q.y;
              const md = item.r + q.r + 2;
              if (dx * dx + dy * dy < md * md) { overlaps = true; break; }
            }
            if (!overlaps) { bestPos = { x: tx, y: ty }; break; }
          }
        }

        placed.push({ x: bestPos?.x ?? cx, y: bestPos?.y ?? cy, r: item.r, wallet: item.wallet });
      }

      // Save positions for next iteration
      prevPositions = new Map();
      for (const p of placed) {
        prevPositions.set(p.wallet.address, { x: p.x, y: p.y, r: p.r });
      }

      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      for (const p of placed) {
        minX = Math.min(minX, p.x - p.r); maxX = Math.max(maxX, p.x + p.r);
        minY = Math.min(minY, p.y - p.r); maxY = Math.max(maxY, p.y + p.r);
      }
      const pad = 10;
      viewBox = `${minX - pad} ${minY - pad} ${(maxX - minX) + pad * 2} ${(maxY - minY) + pad * 2}`;
      bubbleData = placed;
    }, 5000);

    return () => { clearInterval(fastIv); clearInterval(slowIv); };
  });

  function clickBubble(wallet: WalletStat) {
    if (selectedWallet?.address === wallet.address) {
      selectedWallet = null;
    } else {
      selectedWallet = wallet;
    }
  }

  function clickBackground() {
    selectedWallet = null;
  }

  const getWalletType = (w: WalletStat): { label: string; color: string } | null => {
    const avg = w.tradeCount > 0 ? w.quoteVolume / w.tradeCount : 0;
    if (w.tradeCount > 5 && avg < BOT_THRESHOLD) return { label: "Bot", color: "text-red-500" };
    if (avg > WHALE_THRESHOLD) return { label: "Whale", color: "text-blue-500" };
    return null;
  };

  const formatVol = (vol: number) => {
    if (vol >= 1_000_000) return `${(vol / 1_000_000).toFixed(1)}M`;
    if (vol >= 1_000) return `${(vol / 1_000).toFixed(1)}K`;
    return vol.toFixed(2);
  };

  const getColor = (index: number) => {
    if (index === 0) return "#0284c7";    // sky-600
    if (index < 3) return "#0ea5e9";      // sky-500
    if (index < 10) return "#38bdf8";     // sky-400
    if (index < 25) return "#7dd3fc";     // sky-300
    return "#4b5563";                      // gray-600
  };

  const getOpacity = (index: number) => {
    if (index < 3) return 0.95;
    if (index < 10) return 0.8;
    if (index < 25) return 0.65;
    return 0.45;
  };
</script>

<div class="px-4">
  <!-- Widgets Row -->
  <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 mb-6">
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-4 text-center">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">Wallets Seen</p>
      <p class="text-2xl font-black text-white tabular-nums">{totalWallets}</p>
    </div>
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-4 text-center">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">Total Volume</p>
      <p class="text-2xl font-black text-white tabular-nums">{formatVol(totalVolume)}</p>
    </div>
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-4 text-center">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">Top Wallet</p>
      <p class="text-lg font-black text-white tabular-nums font-mono">
        {topWallet ? shortAddress(topWallet.address, 4, 4) : "—"}
      </p>
    </div>
    <div class="bg-gray-950/40 border border-red-900/30 rounded-sm p-4 text-center">
      <p class="text-[10px] font-mono text-red-500/60 uppercase tracking-widest mb-1">Likely Bots</p>
      <p class="text-2xl font-black text-red-500 tabular-nums">{likelyBots}</p>
      <p class="text-[9px] text-gray-600 font-mono mt-1">&gt;5 trades, small avg</p>
    </div>
    <div class="bg-gray-950/40 border border-blue-900/30 rounded-sm p-4 text-center">
      <p class="text-[10px] font-mono text-blue-500/60 uppercase tracking-widest mb-1">Likely Whales</p>
      <p class="text-2xl font-black text-blue-500 tabular-nums">{likelyWhales}</p>
      <p class="text-[9px] text-gray-600 font-mono mt-1">&gt;5 SOL avg trade</p>
    </div>
  </div>

  <!-- Bubble Chart -->
  {#if bubbleData.length > 0}
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-4 mb-6">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-3">Quote Volume by Wallet</p>

      <div class="relative">
        <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
        <svg viewBox={viewBox} class="w-full h-auto" style="max-height: 600px;" onclick={clickBackground}>
          {#each bubbleData as bubble, i (bubble.wallet.address)}
            <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_noninteractive_element_interactions -->
            <g
              onmouseenter={() => { if (!selectedWallet) hoveredWallet = bubble.wallet; }}
              onmouseleave={() => { if (!selectedWallet) hoveredWallet = null; }}
              onclick={(e: MouseEvent) => { e.stopPropagation(); clickBubble(bubble.wallet); }}
              class="cursor-pointer"
            >
              <circle
                cx={bubble.x}
                cy={bubble.y}
                r={bubble.r}
                fill={getColor(i)}
                opacity={activeWallet?.address === bubble.wallet.address ? 1 : getOpacity(i)}
                stroke={activeWallet?.address === bubble.wallet.address ? "#fff" : "transparent"}
                stroke-width="1.5"
              />
              {#if bubble.r > 18}
                <text
                  x={bubble.x}
                  y={bubble.y - 3}
                  text-anchor="middle"
                  dominant-baseline="middle"
                  fill="white"
                  font-size={bubble.r > 40 ? "8" : "6"}
                  font-family="monospace"
                  font-weight="bold"
                  class="pointer-events-none"
                >
                  {shortAddress(bubble.wallet.address, 3, 3)}
                </text>
                <text
                  x={bubble.x}
                  y={bubble.y + 8}
                  text-anchor="middle"
                  dominant-baseline="middle"
                  fill="white"
                  font-size={bubble.r > 40 ? "7" : "5"}
                  font-family="monospace"
                  opacity="0.6"
                  class="pointer-events-none"
                >
                  {formatVol(bubble.wallet.quoteVolume)}
                </text>
              {/if}
            </g>
          {/each}
        </svg>

        <!-- Tooltip (hover or pinned) -->
        {#if activeWallet}
          <div class="absolute top-2 right-2 bg-black border border-gray-700 rounded-sm p-3 text-xs z-10 min-w-[180px]">
            <div class="flex items-center gap-2 mb-2">
              <p class="font-mono text-white">{shortAddress(activeWallet.address, 6, 6)}</p>
              {#if getWalletType(activeWallet)}
                {@const wtype = getWalletType(activeWallet)}
                <span class="text-[9px] font-mono uppercase {wtype?.color}">{wtype?.label}</span>
              {/if}
            </div>
            <div class="space-y-1">
              <p class="text-gray-500">Volume: <span class="text-white">{formatVol(activeWallet.quoteVolume)}</span></p>
              <p class="text-gray-500">Trades: <span class="text-white">{activeWallet.tradeCount}</span></p>
              <p class="text-gray-500">Tokens: <span class="text-white">{activeWallet.tokens.size}</span></p>
              <p class="text-gray-500">Avg Size: <span class="text-white">{formatVol(activeWallet.tradeCount > 0 ? activeWallet.quoteVolume / activeWallet.tradeCount : 0)}</span></p>
            </div>
            <a
              href={`https://solscan.io/account/${activeWallet.address}`}
              target="_blank"
              class="text-sky-500 hover:text-sky-400 mt-2 block"
            >
              View on Solscan
            </a>
            {#if selectedWallet}
              <button onclick={() => selectedWallet = null} class="text-gray-600 hover:text-gray-400 text-[10px] font-mono mt-2 block">
                Dismiss
              </button>
            {/if}
          </div>
        {/if}
      </div>
    </div>
  {:else}
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-8 text-center">
      <p class="text-sm text-gray-600 italic">Waiting for trade data to build wallet chart...</p>
    </div>
  {/if}

  <!-- Min tokens filter -->
  <div class="flex flex-wrap items-center justify-center gap-3 my-4">
    <span class="text-[10px] font-mono text-gray-600 uppercase tracking-widest">Min tokens per wallet</span>
    <input
      type="range"
      min="1"
      max="100"
      bind:value={minTokens}
      class="range range-xs w-full max-w-40"
    />
    <input
      type="number"
      min="1"
      max="100"
      bind:value={minTokens}
      class="input input-xs bg-black border-gray-800 text-white font-mono w-14 rounded-sm text-center"
    />
  </div>

  <!-- Top Wallets Table -->
  {#if sortedWallets.length > 0}
    <div>
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-3">Top Wallets by Volume</p>
      <div class="overflow-x-auto">
        <table class="w-full text-xs font-mono">
          <thead>
            <tr class="text-gray-600 text-left border-b border-gray-800">
              <th class="py-1.5 px-2 font-normal w-8 hidden sm:table-cell">#</th>
              <th class="py-1.5 px-2 font-normal">Address</th>
              <th class="py-1.5 px-2 font-normal text-right">Type</th>
              <th class="py-1.5 px-2 font-normal text-right">Volume</th>
              <th class="py-1.5 px-2 font-normal text-right">Trades</th>
              <th class="py-1.5 px-2 font-normal text-right hidden md:table-cell">Tokens</th>
              <th class="py-1.5 px-2 font-normal text-right hidden md:table-cell">Avg Size</th>
              <th class="py-1.5 px-2 font-normal w-24 hidden sm:table-cell"></th>
            </tr>
          </thead>
          <tbody>
            {#each sortedWallets.slice(0, 20) as wallet, i}
              <tr class="border-b border-gray-800/50 hover:bg-gray-900/50">
                <td class="py-1.5 px-2 text-gray-700 tabular-nums hidden sm:table-cell">{String(i + 1).padStart(2, "0")}</td>
                <td class="py-1.5 px-2">
                  <a href={`https://solscan.io/account/${wallet.address}`} target="_blank" class="text-white hover:text-sky-500 transition-colors">{shortAddress(wallet.address, 6, 6)}</a>
                </td>
                <td class="py-1.5 px-2 text-right">
                  {#if getWalletType(wallet)}
                    {@const wtype = getWalletType(wallet)}
                    <span class="{wtype?.color}">{wtype?.label}</span>
                  {/if}
                </td>
                <td class="py-1.5 px-2 text-right text-white tabular-nums">{formatVol(wallet.quoteVolume)}</td>
                <td class="py-1.5 px-2 text-right text-gray-400 tabular-nums">{wallet.tradeCount}</td>
                <td class="py-1.5 px-2 text-right text-gray-400 tabular-nums hidden md:table-cell">{wallet.tokens.size}</td>
                <td class="py-1.5 px-2 text-right text-gray-400 tabular-nums hidden md:table-cell">{formatVol(wallet.tradeCount > 0 ? wallet.quoteVolume / wallet.tradeCount : 0)}</td>
                <td class="py-1.5 px-2 hidden sm:table-cell">
                  <div class="h-0.5 bg-gray-800 rounded-full w-full">
                    <div class="h-full bg-sky-600 rounded-full" style="width: {(wallet.quoteVolume / (sortedWallets[0]?.quoteVolume || 1)) * 100}%"></div>
                  </div>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </div>
  {/if}
</div>

<style>
  circle {
    transition: cx 1.5s ease-out, cy 1.5s ease-out, r 1s ease-out, opacity 0.3s ease;
  }
</style>
